import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
} from "@mui/material";

function App() {
  const [startBalance, setStartBalance] = useState("");
  const [currentBalance, setCurrentBalance] = useState("");
  const [month, setMonth] = useState("");
  const [annualRate, setAnnualRate] = useState("");
  const [result, setResult] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [error, setError] = useState("");
  const [averageSavings, setAverageSavings] = useState("");

  const validateInputs = () => {
    const startBal = parseFloat(startBalance);
    const currentBal = parseFloat(currentBalance);
    const currentMonth = parseInt(month);
    const rate = parseFloat(annualRate);

    if (isNaN(startBal) || startBal < 0) return "Invalid starting balance.";
    if (isNaN(currentBal) || currentBal < 0) return "Invalid current balance.";
    if (isNaN(currentMonth) || currentMonth < 1 || currentMonth > 12)
      return "Invalid month. Must be between 1 and 12.";
    if (isNaN(rate) || rate < 0) return "Invalid annual rate.";

    return "";
  };

  const calculateMonthlyProfit = () => {
    const errorMsg = validateInputs();
    if (errorMsg) {
      setError(errorMsg);
      setResult(null);
      setMonthlyData([]);
      setAverageSavings("");
      return;
    }

    const startBal = parseFloat(startBalance);
    const currentBal = parseFloat(currentBalance);
    const currentMonth = parseInt(month);
    const rate = parseFloat(annualRate);

    const monthlyRate = rate / 12 / 100;
    const monthsInYear = 12;
    const monthlyIncrement = (currentBal - startBal) / (currentMonth - 1 || 1);

    let totalProfit = 0;
    let previousBalance = startBal;
    let data = [];

    for (let m = 1; m <= monthsInYear; m++) {
      let averageBalance =
        m <= currentMonth
          ? m === 1
            ? startBal
            : (previousBalance + (previousBalance + monthlyIncrement)) / 2
          : previousBalance + monthlyIncrement;

      let monthlyProfit = averageBalance * monthlyRate;
      totalProfit += monthlyProfit;
      previousBalance += monthlyIncrement;

      data.push({
        month: m,
        averageBalance: averageBalance.toFixed(2),
        monthlyProfit: monthlyProfit.toFixed(2),
      });
    }

    const totalYearProfit = totalProfit.toFixed(2);
    const finalBalance = (currentBal + totalProfit).toFixed(2);
    const endOfYearBalance = (previousBalance + totalProfit).toFixed(2);

    // Calculate the average monthly savings
    const totalSaved = currentBal - startBal;
    const averageMonthlySavings = (
      totalSaved / (currentMonth - 1 || 1)
    ).toFixed(2);

    setResult({
      totalYearProfit,
      finalBalance,
      endOfYearBalance,
    });
    setMonthlyData(data);
    setAverageSavings(averageMonthlySavings);
    setError(""); // Clear any previous errors
  };

  return (
    <Container maxWidth="sm" sx={{ marginTop: 4 }}>
      <Paper elevation={3} sx={{ padding: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          ASNB Profit Calculator
        </Typography>
        <Box component="form" noValidate autoComplete="off">
          <TextField
            fullWidth
            label="Starting Balance at Beginning of Year (RM)"
            variant="outlined"
            margin="normal"
            value={startBalance}
            onChange={(e) => setStartBalance(e.target.value)}
            error={!!error}
            helperText={
              error && error.includes("Starting balance") ? error : ""
            }
          />
          <TextField
            fullWidth
            label="Current Balance (RM)"
            variant="outlined"
            margin="normal"
            value={currentBalance}
            onChange={(e) => setCurrentBalance(e.target.value)}
            error={!!error}
            helperText={error && error.includes("Current balance") ? error : ""}
          />
          <TextField
            fullWidth
            label="Current Month (1 for January, 12 for December, etc.)"
            variant="outlined"
            margin="normal"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            error={!!error}
            helperText={error && error.includes("month") ? error : ""}
          />
          <TextField
            fullWidth
            label="Predicted Annual Dividend Rate (%)"
            variant="outlined"
            margin="normal"
            value={annualRate}
            onChange={(e) => setAnnualRate(e.target.value)}
            error={!!error}
            helperText={error && error.includes("annual rate") ? error : ""}
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ marginTop: 2 }}
            onClick={calculateMonthlyProfit}
          >
            Calculate
          </Button>
        </Box>
        {error && (
          <Box sx={{ marginTop: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        {result && (
          <>
            <Box sx={{ marginTop: 4 }}>
              <Typography variant="h6">Results:</Typography>
              <Typography>
                Current Balance after Adding Profit: RM{result.finalBalance}
              </Typography>
              <Typography>
                Current Profit: RM{result.totalYearProfit}
              </Typography>
              <Typography>
                Projected End-of-Year Balance: RM{result.endOfYearBalance}
              </Typography>
              <Typography>
                Average Money Saved Each Month: RM{averageSavings}
              </Typography>
            </Box>
            <Box sx={{ marginTop: 4 }}>
              <Typography variant="h6">Monthly Breakdown:</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Month</TableCell>
                    <TableCell align="center">Average Balance (RM)</TableCell>
                    <TableCell align="center">Profit Earned (RM)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthlyData.map((data) => (
                    <TableRow key={data.month}>
                      <TableCell align="center">{data.month}</TableCell>
                      <TableCell align="center">
                        {data.averageBalance}
                      </TableCell>
                      <TableCell align="center">{data.monthlyProfit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
}

export default App;
